import {Fragment} from "react";
import usePostUserFeedback from "@/hooks/usePostUserFeedback";
import {ModalDialog, ModalDialogProps} from "../ModalDialog";
import RippleButton from "../RippleButton";
import Input from "../Input";
import {useForm} from "@tanstack/react-form";
import ErrorBox from "../ErrorBox";
import SuccessBox from "../SuccessBox";
import {z} from "zod";
import {sleep} from "@/utils/async";
import {getErrorText} from "@/utils/form";
import {isSafeHref} from "@/utils/url";
import {sanitize} from "@/utils/sanitize";
import {FAQ_ITEMS, FaqItem as FaqItemType, FeedbackContext} from "./faq";
import styles from "./index.module.css";

// Splits a line into text + markdown-link `[label](href)` segments.
const renderLine = (line: string, keyPrefix: string): React.ReactNode[] => {
  const tokens = line.split(/(\[[^\]]+\]\([^)]+\))/g).filter(Boolean);
  return tokens.map((token, i) => {
    const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!linkMatch)
      return <Fragment key={`${keyPrefix}-t-${i}`}>{sanitize(token)}</Fragment>;
    const [, label, href] = linkMatch;
    if (!isSafeHref(href)) {
      return <span key={`${keyPrefix}-t-${i}`}>{token}</span>;
    }
    const isExternal = /^https?:\/\//.test(href);
    return (
      <a
        key={`${keyPrefix}-a-${i}`}
        href={href}
        {...(isExternal ? {target: "_blank", rel: "noopener noreferrer"} : {})}
        className="text-primary-container underline"
      >
        {label}
      </a>
    );
  });
};

const renderAnswer = (text: string) => {
  return text.split("::BR::").map((line, lineIndex, lines) => (
    <span key={lineIndex}>
      {renderLine(line, String(lineIndex))}
      {lineIndex < lines.length - 1 && <br />}
    </span>
  ));
};

interface FeedbackDialogProps extends Omit<
  ModalDialogProps,
  "children" | "onClose"
> {
  // Required: the dialog must always be dismissible via UI controls.
  onClose: NonNullable<ModalDialogProps["onClose"]>;
  // Optional initial message to pre-fill the feedback form (e.g. FlockPage support flow).
  message?: string;
  // Optional context determining which FAQ list is shown above the form.
  // When omitted (or when `message` is pre-filled), no FAQ block is rendered.
  context?: FeedbackContext;
}

const FaqItem = ({item}: {item: FaqItemType}) => (
  <details name="feedback-faq" className="group">
    <summary
      className={[
        "gap-g-1 flex cursor-pointer list-none items-center",
        "[&::-webkit-details-marker]:hidden",
        "hover:text-primary-container",
        "group-open:text-primary-container",
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={[
          "flex size-5 flex-none items-center justify-center rounded-full border text-sm font-semibold",
          "border-on-surface-variant text-on-surface-variant",
          "group-open:border-primary-container group-open:bg-primary-container group-open:text-white",
        ].join(" ")}
      >
        ?
      </span>
      <span className="font-medium group-open:font-semibold">
        {sanitize(item.question)}
      </span>
    </summary>
    <div className="text-on-surface-variant pr-2 pl-7 leading-normal">
      {renderAnswer(item.answer)}
    </div>
  </details>
);

const FeedbackDialog = (props: FeedbackDialogProps) => {
  const postUserFeedback = usePostUserFeedback();

  const form = useForm({
    defaultValues: {
      message: props?.message || "",
    },
    onSubmit: async ({value}) => {
      await postUserFeedback.mutateAsync(value.message);

      // wait for 2 seconds before closing the dialog
      await sleep(2000);
      props.onClose("submit");

      // reset the form after closing the dialog
      await sleep(1000);
      form.reset();
      postUserFeedback.reset();
    },
  });

  // Reset on any close (X / backdrop / ESC / Cancel) so a dismissed draft
  // doesn't leak into the next dialog open. Skipped while submitting or while
  // the success state is showing — the submit flow handles its own cleanup.
  const handleClose = (source: string) => {
    if (!postUserFeedback.isPending && !postUserFeedback.isSuccess) {
      form.reset();
      postUserFeedback.reset();
    }
    props.onClose(source);
  };

  const handleCancel = () => handleClose("cancel-button");

  const showFaq = !props?.message && props?.context !== undefined;
  const faqItems = showFaq && props.context ? FAQ_ITEMS[props.context] : null;

  return (
    <ModalDialog
      hasCloseBtn={true}
      header="Napište nám"
      text={
        props?.message
          ? ""
          : "Něco nefunguje? Něco má být jinak? Máte nápad, jak Trendaro vylepšit?::BR::::BR::Popište nám svůj problém s dotazníkem, nebo co byste chtěli upravit."
      }
      {...props}
      onClose={handleClose}
    >
      {faqItems && (
        <section className="mt-g-2 xs:mt-g-3 border-outline-variant bg-surface-container p-g-2 pl-g-1 rounded-md border">
          <div className="xs:gap-g-1 flex items-start gap-1">
            <span
              aria-hidden="true"
              className={`xs:text-xl flex-none text-base leading-none ${styles.bulbGlow}`}
            >
              💡
            </span>
            <div className="flex flex-col">
              <p className="mb-g-2 leading-normal">
                <strong>Máte dotaz?</strong> Možná už máme odpověď{" "}
                <span aria-hidden="true">👇</span>
              </p>
              <p className="mb-g-1 font-semibold">Nejčastější otázky:</p>
              <ul className="flex flex-col">
                {faqItems.map((item) => (
                  <li key={item.question} className="pb-g-1 last:pb-0">
                    <FaqItem item={item} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="mt-g-3 flex flex-col items-center"
      >
        <form.Field
          name="message"
          validators={{
            onChange: z
              .string()
              .trim()
              .min(2, "Zpráva musí mít délku alespoň 2 znaky.")
              .max(1000, "Zpráva může mít maximálně 1000 znaků."),
          }}
          children={(field) => {
            return (
              <Input
                className="min-h-28 w-full"
                label="Zpráva"
                multiline={true}
                errorText={getErrorText(field)}
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
            );
          }}
        />
        <div className="xs:px-g-1 sm:px-g-3 gap-g-3 mt-g-2 xs:mt-g-3 flex flex-col items-center justify-center">
          <div className="gap-g-2 flex flex-row flex-wrap items-center justify-center">
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <RippleButton
                  type="submit"
                  variant="primary"
                  disabled={!canSubmit}
                  isPending={isSubmitting}
                >
                  Odeslat
                </RippleButton>
              )}
            />
            <RippleButton
              type="button"
              variant="text"
              onClick={handleCancel}
              disabled={postUserFeedback.isPending}
              className="underline"
            >
              Zavřít, nechci nic poslat
            </RippleButton>
          </div>
          <ErrorBox error={postUserFeedback.error} />
          {postUserFeedback.isSuccess && (
            <SuccessBox text="Zpráva byla odeslána." />
          )}
        </div>
      </form>
    </ModalDialog>
  );
};

export default FeedbackDialog;
